import type { ApplicationAudio } from "../audio/applicationAudio"
import { type BinauralPlan, binauralConfigSchema } from "../audio/binauralPlan"
import { buttonElement, htmlElement, inputElement, selectElement } from "./dom"

type AudioProvider = () => ApplicationAudio
type ErrorHandler = (cause: unknown) => void

function readGeneratorConfig() {
  const common = {
    carrierHertz: inputElement("carrier").valueAsNumber,
    levelDecibels: inputElement("generator-level").valueAsNumber,
    waveform: selectElement("waveform").value,
  }
  const value =
    selectElement("generator-mode").value === "spatial"
      ? {
          ...common,
          mode: "spatial",
          azimuthDegrees: inputElement("azimuth").valueAsNumber,
          elevationDegrees: inputElement("elevation").valueAsNumber,
        }
      : { ...common, mode: "beat", beatHertz: inputElement("beat").valueAsNumber }
  return binauralConfigSchema.safeParse(value)
}

function describePlan(plan: BinauralPlan): string {
  switch (plan.kind) {
    case "beat":
      return `Playing binaural beat: ${plan.leftHertz} Hz left, ${plan.rightHertz} Hz right.`
    case "spatial":
      return `Playing HRTF spatial source at ${inputElement("azimuth").valueAsNumber}° azimuth.`
  }
}

export function bindGeneratorControls(provider: AudioProvider, onError: ErrorHandler): void {
  let playing = false

  const toggleGenerator = async (): Promise<void> => {
    const app = provider()
    const button = buttonElement("generator-toggle")
    if (playing) {
      app.stopBinaural()
      playing = false
      button.textContent = "Start generator"
      htmlElement("generator-status").textContent = "Generator stopped."
      return
    }
    const parsed = readGeneratorConfig()
    if (!parsed.success) {
      htmlElement("generator-status").textContent =
        parsed.error.issues[0]?.message ?? "Invalid generator settings."
      return
    }
    await app.resume()
    const plan = app.startBinaural(parsed.data)
    playing = true
    button.textContent = "Stop generator"
    htmlElement("generator-status").textContent = describePlan(plan)
  }

  const updateGenerator = (): void => {
    htmlElement("carrier-value").textContent = `${inputElement("carrier").valueAsNumber} Hz`
    htmlElement("beat-value").textContent = `${inputElement("beat").valueAsNumber.toFixed(1)} Hz`
    htmlElement("generator-level-value").textContent =
      `${inputElement("generator-level").valueAsNumber} dBFS`
    htmlElement("azimuth-value").textContent = `${inputElement("azimuth").valueAsNumber}°`
    htmlElement("elevation-value").textContent = `${inputElement("elevation").valueAsNumber}°`
    const parsed = readGeneratorConfig()
    if (playing && parsed.success) {
      htmlElement("generator-status").textContent = describePlan(
        provider().updateBinaural(parsed.data),
      )
    }
  }

  buttonElement("generator-toggle").addEventListener(
    "click",
    () => void toggleGenerator().catch(onError),
  )
  for (const id of [
    "carrier",
    "beat",
    "generator-level",
    "azimuth",
    "elevation",
    "waveform",
    "generator-mode",
  ]) {
    htmlElement(id).addEventListener("input", updateGenerator)
    htmlElement(id).addEventListener("change", updateGenerator)
  }
}
